import Icon, { Icons } from '@/presentation/components/elements/Icon';
import InfiniteScroll from '@/presentation/components/modules/InfiniteScroll';
import Modal from '@/presentation/components/modules/Modal';
import VaultSwitcher from '@/presentation/components/modules/VaultSwitcher';
import CurrentRefinements from '@/presentation/components/modules/filters/CurrentRefinements';
import FilterToggle from '@/presentation/components/modules/filters/FilterToggle';
import Filters from '@/presentation/components/modules/filters/Filters';
import { Filter } from '@/presentation/hooks/filters/useTraitFilters';
import { Asset, TokenId, Vault } from 'nftx.js';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { QueryStatus } from '@tanstack/react-query';
import { useTranslations } from 'use-intl';
import MarketplaceAsset from './MarketplaceAsset';
import Button from '@/presentation/components/elements/Button';
import ChoiceButton from '@/presentation/components/elements/ChoiceButton';
import { useModalContext } from '@/presentation/context/modal/ModalContext';
import { FilterModal } from '@/presentation/components/modals/FilterModal';

export interface MarketplaceProps {
  showBasket: boolean;
  setShowBasket: (v: boolean) => void;
  swap: ReactNode;
  tokenIds: [TokenId, number][];
  selectedVault: Vault | undefined;
  onSelectVault: (vault: Vault | undefined) => void;
  assets?: Asset[];
  assetsStatus: QueryStatus;
  onToggleTokenId: (tokenId: TokenId) => void;
  vault?: Vault;
  vaults?: Vault[];
  filters: Filter[];
  onFilter: (key: string, value: string) => void;
  hasFilters: boolean;
  canFilter: boolean;
  noLiquidity: boolean;
  onQuantityChange: (tokenId: TokenId, quantity: number) => void;
}

const LIMIT = 16;

const Buy = ({
  swap,
  assets,
  assetsStatus,
  tokenIds,
  onToggleTokenId,
  vault,
  vaults = [],
  onSelectVault,
  selectedVault,
  setShowBasket,
  showBasket,
  filters,
  onFilter,
  hasFilters,
  canFilter,
  noLiquidity,
  onQuantityChange,
}: MarketplaceProps) => {
  const { showCreateLiquidityPositionModal } = useModalContext();
  const [showFilters, setShowFilters] = useState(false);
  const t = useTranslations('Collection');
  const showBasketFirstTimeRef = useRef(true);
  const [visibleIndex, setVisibleIndex] = useState(LIMIT);

  const visibleAssets = assets?.slice(0, visibleIndex);
  const scrollable = !!(
    assets &&
    visibleAssets &&
    assets.length > visibleAssets.length
  );

  useEffect(() => {
    if (tokenIds.length && showBasketFirstTimeRef.current) {
      setShowBasket(true);
      showBasketFirstTimeRef.current = false;
    }
  }, [setShowBasket, tokenIds]);

  const hasMultipleVaults = vaults?.length > 1;
  const assetCount = assets?.length || 0;

  const status = useMemo(() => {
    if (assetsStatus === 'loading') {
      return 'loading';
    }

    if (assetsStatus === 'success') {
      if (assetCount == 0) {
        if (hasFilters) {
          return 'no-match';
        }

        return 'no-assets';
      }

      if (noLiquidity) {
        return 'no-liquidity';
      }
    }
  }, [assetsStatus, assetCount, noLiquidity, hasFilters]);

  return (
    <div>
      <header className="flex mb-6 items-center space-x-4">
        {hasMultipleVaults && (
          <div>
            <VaultSwitcher
              vaults={vaults}
              selected={selectedVault}
              setSelected={onSelectVault}
              showAddress={false}
            />
          </div>
        )}
        <p className="text-gray-500 font-medium flex-grow">
          {t('items', { count: assets?.length || 0 })}
        </p>
        <FilterToggle
          canFilter={canFilter}
          hasFilters={hasFilters}
          onClick={() => setShowFilters((x) => !x)}
          showFilters={showFilters}
        />
      </header>
      <div className={hasFilters ? 'pb-4' : ''}>
        <FilterModal
          showFilters={showFilters}
          filters={filters}
          onFilter={onFilter}
          onClose={() => setShowFilters(false)}
        />
        {hasFilters && (
          <CurrentRefinements filters={filters} onFilter={onFilter} />
        )}
      </div>
      {status === 'loading' && (
        <div className="p-6 flex justify-center items-center">
          <Icon name={Icons.SPINNER_GAP} className="animate-spin" size={24} />
        </div>
      )}
      {status === 'no-match' && (
        <p className="text-center p-6">
          {`There are no matching assets within NFTX for this collection.`}
        </p>
      )}
      {status === 'no-assets' && (
        <div className="text-center space-y-4 p-6">
          <Icon
            name={Icons.GHOST}
            className="text-pink-500 inline-block"
            size={48}
          />
          <h4 className="font-bold text-lg">{'No items in vault.'}</h4>
          <p className="text-center">
            {'Deposit to kickstart this vault and be a first earner.'}
          </p>
          {vault ? (
            <Button
              size="small"
              className="inline-flex"
              onClick={() =>
                showCreateLiquidityPositionModal({
                  vaultId: vault.vaultId,
                  using: 'nft',
                  poolType: 'nft',
                })
              }
            >
              {'Add to pool'}
            </Button>
          ) : vaults.length > 0 ? (
            vaults.length === 1 ? (
              <Button
                size="small"
                className="inline-flex"
                onClick={() =>
                  showCreateLiquidityPositionModal({
                    vaultId: vaults[0].vaultId,
                    using: 'nft',
                    poolType: 'nft',
                  })
                }
              >
                {'Add to pool'}
              </Button>
            ) : (
              <div>
                <ChoiceButton
                  size="small"
                  options={vaults?.map((x) => ({
                    label: x.token.name,
                    onClick: () =>
                      showCreateLiquidityPositionModal({
                        vaultId: x.vaultId,
                        using: 'nft',
                        poolType: 'nft',
                      }),
                  }))}
                >
                  {'Add to pool'}
                </ChoiceButton>
              </div>
            )
          ) : null}
        </div>
      )}
      {status === 'no-liquidity' && (
        <div className="text-center space-y-4 p-6">
          <Icon
            name={Icons.DROP_HALF_BOTTOM}
            className="text-pink-500 inline-block"
            size={48}
          />
          <h4 className="font-bold text-lg">{'No liquidity in vault.'}</h4>
          <p className="text-center">
            {'Deposit NFTs into the liquidity pool to allow for trading.'}
          </p>
          {vault ? (
            <Button
              size="small"
              className="inline-flex"
              onClick={() =>
                showCreateLiquidityPositionModal({
                  vaultId: vault.vaultId,
                  using: 'nft',
                  poolType: 'nft',
                })
              }
            >
              {'Add to pool'}
            </Button>
          ) : vaults.length > 0 ? (
            vaults.length === 1 ? (
              <Button
                size="small"
                className="inline-flex"
                onClick={() =>
                  showCreateLiquidityPositionModal({
                    vaultId: vaults[0].vaultId,
                    using: 'nft',
                    poolType: 'nft',
                  })
                }
              >
                {'Add to pool'}
              </Button>
            ) : (
              <div>
                <ChoiceButton
                  size="small"
                  options={vaults?.map((x) => ({
                    label: x.token.name,
                    onClick: () =>
                      showCreateLiquidityPositionModal({
                        vaultId: x.vaultId,
                        using: 'nft',
                        poolType: 'nft',
                      }),
                  }))}
                >
                  {'Add to pool'}
                </ChoiceButton>
              </div>
            )
          ) : null}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-4 pb-12">
        <InfiniteScroll
          onLoadMore={() => setVisibleIndex((x) => x + LIMIT)}
          scrollable={scrollable}
        >
          {visibleAssets?.map((ass) => {
            const item = tokenIds.find(([tokenId]) => tokenId === ass.tokenId);
            const selected = !!item;
            const selectedQuantity = item?.[1] ?? 1;
            const disabled =
              !!vault && ass.vaultIds.includes(vault.vaultId) === false;

            return (
              <MarketplaceAsset
                key={ass.id}
                {...ass}
                onClick={() => onToggleTokenId(ass.tokenId)}
                selected={selected}
                disabled={disabled}
                onQuantityChange={(quantity) =>
                  onQuantityChange(ass.tokenId, quantity)
                }
                selectedQuantity={selectedQuantity}
              />
            );
          })}
        </InfiniteScroll>
      </div>
      <div
        className={`fixed top-full transform transition-transform sm:w-full sm:max-w-md ${
          showBasket
            ? '-translate-y-full'
            : '-translate-y-[7.5rem] sm:-translate-y-16'
        } right-2 left-2 sm:left-auto md:right-4 z-2`}
      >
        <div className="rounded-t-xl shadow-lg pb-10 sm:pb-0 bg-gray-50 relative">
          <button
            type="button"
            className={`absolute w-full text-center z-10 flex justify-center -mb-6 pt-1 ${
              !showBasket ? 'h-full' : 'h-6'
            }`}
            onClick={() => setShowBasket(!showBasket)}
          >
            <Icon
              name={Icons.CHEVRON_UP}
              size={24}
              className={`transform will-change-transform text-gray-500 ${
                showBasket ? 'rotate-180' : ''
              } transition-transform`}
            />
          </button>
          {swap}
        </div>
      </div>
    </div>
  );
};

export default Buy;
